// ui/ReportModal.js
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon, FlagIcon } from "@heroicons/react/24/outline";

const REPORT_REASONS = [
  { id: "spam", label: "Spam or misleading" },
  { id: "harassment", label: "Harassment or hate speech" },
  { id: "inappropriate", label: "Inappropriate content" },
  { id: "false-info", label: "False information" },
  { id: "other", label: "Other" },
];

/**
 * Modal for reporting notes or comments
 *
 * @param {boolean} open - Controls modal visibility
 * @param {function} onClose - Called when user cancels or clicks backdrop
 * @param {function} onSubmit - Called with (reason, details) when user submits
 * @param {string} type - Type of content being reported: "note" or "comment"
 * @param {boolean} submitting - Whether the report is currently being submitted
 */
export default function ReportModal({
  open,
  onClose,
  onSubmit,
  type = "note",
  submitting = false,
}) {
  const [selectedReason, setSelectedReason] = useState("");
  const [details, setDetails] = useState("");

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedReason) return;

    onSubmit(selectedReason, details);

    // Reset form
    setSelectedReason("");
    setDetails("");
  };

  const handleClose = () => {
    // Reset form on close
    setSelectedReason("");
    setDetails("");
    onClose();
  };

  const modal = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={handleClose}
      />

      {/* Modal Card */}
      <div className="relative w-full max-w-md rounded-2xl bg-surface-elevated shadow-2xl overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-3 border-b border-border-strong bg-primary">
          <div className="flex items-center gap-2 text-text-inverse">
            <FlagIcon className="h-5 w-5" />
            <h3 className="text-sm font-semibold">Report {type}</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-primary-100"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5 text-primary" />
          </button>
        </header>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <p className="text-sm text-text-secondary">
            Why are you reporting this {type}?
          </p>

          {/* Radio buttons for reasons */}
          <div className="space-y-2">
            {REPORT_REASONS.map((reason) => (
              <button
                key={reason.id}
                type="button"
                role="radio"
                aria-checked={selectedReason === reason.id}
                onClick={() => setSelectedReason(reason.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  selectedReason === reason.id
                    ? "border-border-strong bg-primary text-text-inverse"
                    : "border-border-light bg-surface-elevated hover:bg-surface text-text-secondary"
                }`}
              >
                {/* Custom radio circle indicator */}
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedReason === reason.id
                      ? "border-text-inverse bg-text-inverse"
                      : "border-border-strong"
                  }`}
                >
                  {selectedReason === reason.id && (
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  )}
                </div>
                <span className="text-sm">
                  {reason.label}
                </span>
              </button>
            ))}
          </div>

          {/* Optional details textarea */}
          <div>
            <label
              htmlFor="report-details"
              className="block text-sm font-medium text-text-secondary mb-1"
            >
              Additional details (
              {selectedReason === "other" ? "required" : "optional"})
            </label>
            <textarea
              id="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Provide any additional context..."
              rows={3}
              className="w-full px-3 py-2 bg-surface-elevated border border-border-strong rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              className="px-4 py-2 rounded-md border border-border-strong text-sm font-medium hover:bg-surface transition-colors"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-primary hover:bg-primary text-text-inverse text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={
                !selectedReason ||
                submitting ||
                (selectedReason === "other" && !details.trim())
              }
            >
              {submitting ? "Submitting..." : "Submit Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
